import type { ReactNode } from 'react';

export interface FieldOption {
  value: string | number;
  label: string;
}

export interface FieldSpec {
  section?: string;
  key?: string;
  label?: string;
  type?: 'text' | 'number' | 'select' | 'checkbox' | 'textarea' | 'color';
  options?: FieldOption[];
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  hint?: string;
  half?: boolean;
  showIf?: { key: string; eq: string };
  value?: unknown;
}

type FieldRow =
  | { kind: 'section'; field: FieldSpec }
  | { kind: 'single'; field: FieldSpec }
  | { kind: 'pair'; fields: [FieldSpec, FieldSpec] };

function coerce(field: FieldSpec, raw: unknown): boolean | number | null | string {
  if (field.type === 'checkbox') return !!raw;
  if (field.type === 'number') return raw === '' || raw == null ? null : parseFloat(raw as string);
  return raw == null ? '' : raw as string;
}

export function initialValuesFromFields(fields: FieldSpec[]): Record<string, boolean | number | null | string> {
  const values: Record<string, boolean | number | null | string> = {};
  fields.forEach((field) => {
    if (field.section != null || !field.key) return;
    values[field.key] = coerce(field, field.value);
  });
  return values;
}

export function groupFieldRows(fields: FieldSpec[]): FieldRow[] {
  const rows: FieldRow[] = [];
  let pending: FieldSpec[] = [];
  const flush = () => {
    if (!pending.length) return;
    if (pending.length === 2) rows.push({ kind: 'pair', fields: pending as [FieldSpec, FieldSpec] });
    else rows.push({ kind: 'single', field: pending[0] });
    pending = [];
  };
  fields.forEach((field) => {
    if (field.section != null) {
      flush();
      rows.push({ kind: 'section', field });
      return;
    }
    if (field.half) {
      pending.push(field);
      if (pending.length === 2) flush();
      return;
    }
    flush();
    rows.push({ kind: 'single', field });
  });
  flush();
  return rows;
}

function fieldDomId(field: FieldSpec, idPrefix: string): string {
  return `f-${idPrefix}${field.key}`;
}

function isFieldVisible(field: FieldSpec, values: Record<string, boolean | number | null | string>): boolean {
  if (!field.showIf) return true;
  return String(values[field.showIf.key]) === String(field.showIf.eq);
}

function FieldItem({ field, idPrefix, values, onChange }: { field: FieldSpec; idPrefix: string; values: Record<string, boolean | number | null | string>; onChange: (key: string, value: boolean | number | null | string) => void }) {
  const id = fieldDomId(field, idPrefix);
  const key = field.key as string;
  const value = values[key];
  const hidden = !isFieldVisible(field, values);
  const style = hidden ? { display: 'none' } : undefined;
  const emit = (raw: unknown) => onChange(key, coerce(field, raw));

  if (field.type === 'checkbox') {
    return (
      <label className="fld cbfld" style={style}>
        <input
          type="checkbox"
          id={id}
          checked={!!value}
          onChange={(e) => emit(e.target.checked)}
        />
        {' '}{field.label}
      </label>
    );
  }

  const textValue = value as number | null | string;

  let control: ReactNode;
  if (field.type === 'select') {
    control = (
      <select id={id} value={textValue == null ? '' : textValue} onChange={(e) => emit(e.target.value)}>
        {(field.options || []).map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  } else if (field.type === 'textarea') {
    control = (
      <textarea
        id={id}
        rows={2}
        value={textValue == null ? '' : textValue}
        onChange={(e) => emit(e.target.value)}
      />
    );
  } else if (field.type === 'number') {
    control = (
      <input
        type="number"
        id={id}
        value={textValue == null ? '' : textValue}
        min={field.min}
        max={field.max}
        step={field.step || 1}
        onChange={(e) => emit(e.target.value)}
      />
    );
  } else if (field.type === 'color') {
    control = (
      <input
        type="color"
        id={id}
        value={(textValue as string) || '#000000'}
        onChange={(e) => emit(e.target.value)}
      />
    );
  } else {
    control = (
      <input
        type="text"
        id={id}
        value={textValue == null ? '' : textValue}
        placeholder={field.placeholder || ''}
        autoComplete="off"
        onChange={(e) => emit(e.target.value)}
      />
    );
  }

  return (
    <div className="fld" style={style}>
      <label className="flbl" htmlFor={id}>{field.label}</label>
      {control}
      {field.hint ? <span className="hint2">{field.hint}</span> : null}
    </div>
  );
}

export function FormFields({ fields, values, onChange, idPrefix = '' }: { fields: FieldSpec[]; values: Record<string, boolean | number | null | string>; onChange: (key: string, value: boolean | number | null | string) => void; idPrefix?: string }) {
  const rows = groupFieldRows(fields);
  return (
    <div className="fgrid">
      {rows.map((row, i) => {
        if (row.kind === 'section') {
          return <div className="sechd" key={`sec-${i}`}>{row.field.section}</div>;
        }
        if (row.kind === 'pair') {
          const [a, b] = row.fields;
          return (
            <div className="frow" key={`${a.key}_${b.key}`}>
              <FieldItem field={a} idPrefix={idPrefix} values={values} onChange={onChange} />
              <FieldItem field={b} idPrefix={idPrefix} values={values} onChange={onChange} />
            </div>
          );
        }
        return (
          <FieldItem
            key={row.field.key || `field-${i}`}
            field={row.field}
            idPrefix={idPrefix}
            values={values}
            onChange={onChange}
          />
        );
      })}
    </div>
  );
}

export function FormFooter({
  onCancel,
  onSave,
  onDelete,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  deleteLabel = 'Delete',
  saveDisabled = false,
}: {
  onCancel: () => void;
  onSave: () => void;
  onDelete?: () => void;
  saveLabel?: string;
  cancelLabel?: string;
  deleteLabel?: string;
  saveDisabled?: boolean;
}) {
  return (
    <div className="mact">
      {onDelete ? (
        <button type="button" className="mbtn warn" onClick={onDelete}>{deleteLabel}</button>
      ) : null}
      <button type="button" className="mbtn ghost" onClick={onCancel}>{cancelLabel}</button>
      <button type="button" className="mbtn" onClick={onSave} disabled={saveDisabled}>{saveLabel}</button>
    </div>
  );
}
